import { useEffect, useId, useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { ArrowCircle } from '@revenuemarket/ui';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { Typography } from 'antd';

import { useCategories } from '~hooks/queries/categories/query';
import { useTransaction } from '~hooks/queries/transactions/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { AccountType } from '~models/bank/enum';
import { CashFlowType } from '~models/cashFlow';
import { TransactionsModel } from '~models/transactions';

import SettlementCompletedButton from './SettlementCompletedButton';
import SettlementListDrawerWrapper from './SettlementListDrawerWrapper';
import useSettlementMappingStore, { MappingTransaction } from './SettlementMappingStore';

const cellStyle = {
  padding: '12px 8px',
};

interface ItemProps {
  detail: TransactionsModel.LabelData;
  transaction: TransactionsModel.TransactionDetail | undefined;
  selected?: boolean;
  status: 'NOT_SELECTED' | 'SELECTED' | 'COMPLETED';
  target?: MappingTransaction;
}

const SettlementSplittedItem: React.FC<ItemProps> = ({ detail, transaction, target, status }) => {
  const [price, setPrice] = useState<number>(Math.abs(detail.amount ?? 0));

  useEffect(() => {
    if (detail.amount) {
      setPrice(Math.abs(detail.amount ?? 0));
    }
  }, [detail.amount]);

  const { setMappingTransaction, mappingTransactions } = useSettlementMappingStore();
  const { companyId } = useDemoAwareCompanyId();
  const { data } = useCategories({
    companyId,
  });
  const categories = data?.Data?.data?.categories || [];

  if (!transaction) return null;

  const handleSelect = () => {
    setMappingTransaction({
      transactionId: transaction?.transactionId,
      itemCashFlowType: (detail.amount > 0 ? 'IN' : 'OUT') as CashFlowType,
      cashFlowType: (detail.amount > 0 ? 'IN' : 'OUT') as CashFlowType,
      transactionAt: transaction?.transactionAt,
      transactionName: transaction?.transactionName,
      transactionDescription: transaction?.transactionDescription,
      totalAmount: Math.abs(
        transaction?.transactionInKrwAmount || transaction?.transactionOutKrwAmount,
      ),
      mappingAmount: price,
      categoryLabelKey: detail.categoryId + '_' + detail.companyDetailedCategoryId,
      groupLabelId: detail.groupLabelId,
      labelId: detail.labelId,
      labelAmount: transaction.transactionInAmount || transaction.transactionOutAmount,
      isFx: transaction.currencyCode !== 'KRW',
    });
  };

  const categoryDescription = categories.find(
    (category) =>
      category.categoryLabelKey === detail.categoryId + '_' + detail.companyDetailedCategoryId,
  )?.category3;

  return (
    <Flex
      style={{
        height: 56,
      }}
      align="center"
    >
      <div
        style={{
          width: 'calc(100% - 698px)',
          ...cellStyle,
        }}
      />
      <div
        style={{
          width: 192,
          ...cellStyle,
        }}
      >
        {categoryDescription}
      </div>
      <div
        style={{
          width: 80,
          ...cellStyle,
        }}
      >
        <Flex align="center" gap={4}>
          {detail.amount > 0 ? (
            <>
              <ArrowCircle fill={'#4DC987'} />
              <Typography.Text>입금</Typography.Text>
            </>
          ) : (
            <>
              <ArrowCircle
                fill={'#FF7A70'}
                style={{
                  transform: 'rotate(180deg)',
                }}
              />
              <Typography.Text>출금</Typography.Text>
            </>
          )}
        </Flex>
      </div>
      <div
        style={{
          width: 140,
          textAlign: 'right',
          ...cellStyle,
        }}
      >
        {Math.abs(detail.amount).toLocaleString() + '원'}
      </div>
      <Flex
        style={{
          width: 288,
          textAlign: 'right',
          ...cellStyle,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginRight: 10,
            width: 160,
            textAlign: 'right',
          }}
        >
          {status !== 'NOT_SELECTED' ? (
            <Typography.Text disabled>{`${
              status === 'COMPLETED'
                ? Math.abs(detail.amount).toLocaleString()
                : target?.mappingAmount ?? 0
            }원`}</Typography.Text>
          ) : (
            <FormattedInput
              style={{
                width: 160,
              }}
              className={css`
                & input {
                  text-align: right;
                }
              `}
              value={price + ''}
              onChange={(value) => {
                setPrice(Number(value));
              }}
              suffix="원"
              formatter={(value: string) => {
                if (!value || isNaN(parseInt(value, 10))) return '';
                return parseInt(value, 10).toLocaleString();
              }}
              deformatter={(value: string) => {
                if (!value || isNaN(parseInt(value, 10))) return '';
                return value.replace(/,/g, '');
              }}
            />
          )}
        </div>
        {status !== 'NOT_SELECTED' ? (
          status === 'COMPLETED' ? (
            <SettlementCompletedButton
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => (
                  <SettlementListDrawerWrapper
                    transactionId={transaction.transactionId}
                    labelId={detail.labelId}
                    open={isOpen}
                    onClose={onClose}
                  />
                ));
              }}
            />
          ) : (
            <Typography.Text disabled>선택됨</Typography.Text>
          )
        ) : (
          <Button onClick={handleSelect}>
            <Flex gap={8} align="center">
              <Typography.Text>선택</Typography.Text>
              <RightOutlined />
            </Flex>
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

interface Props {
  transactionId: number;
  accountType: AccountType;
  originalMappingTransactions: MappingTransaction[];
}

const SettlementSplittedList: React.FC<Props> = ({
  transactionId,
  accountType,
  originalMappingTransactions,
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const id = useId();
  const { data } = useTransaction({ companyId, transactionId, accountType });
  const { mappingTransactions } = useSettlementMappingStore();
  const transactionDetail = data?.Data?.data;

  const splittedLabelInfo =
    transactionDetail?.labels?.map((item) => {
      return {
        ...item,
      };
    }) || [];

  const getStatus = (item: TransactionsModel.LabelData) => {
    if (
      (item?.mappedObjCount ?? 0) > 0 &&
      !originalMappingTransactions.some(
        (mappingTransaction) =>
          mappingTransaction.transactionId === transactionDetail?.transactionId &&
          mappingTransaction.labelId === item.labelId,
      )
    ) {
      return 'COMPLETED';
    }

    if (
      mappingTransactions.some(
        (mappingTransaction) =>
          mappingTransaction.transactionId === transactionDetail?.transactionId &&
          mappingTransaction.labelId === item.labelId,
      )
    ) {
      return 'SELECTED';
    }

    return 'NOT_SELECTED';
  };

  return (
    <Flex
      direction="column"
      style={{
        width: '100%',
      }}
      id={id}
    >
      {splittedLabelInfo.map((item) => {
        const target = mappingTransactions.find(
          (mappingTransaction) =>
            mappingTransaction.transactionId === transactionDetail?.transactionId &&
            mappingTransaction.labelId === item.labelId,
        );

        return (
          <SettlementSplittedItem
            detail={item}
            key={item.labelId}
            transaction={transactionDetail}
            status={getStatus(item)}
            target={target ? { ...target } : undefined}
          />
        );
      })}
    </Flex>
  );
};

export default SettlementSplittedList;
